// apps/client/src/hooks/pipelineTracker/useBrokerRoster.ts
//
// Pipeline Tracker: reading and editing the broker roster.
//
// WHY EVERY PRACTICE NEEDS ITS OWN. Nothing in a Salestrekker export marks who
// is a broker, and the demos this week showed every SFG practice organising
// itself differently: some put support staff on deal cards, some run acquired
// books under the original brokers' names, some do both. There is no rule the
// product could infer, so the practice states it once and the page obeys.
//
// The server takes the WHOLE document plus the version the client loaded, so
// every save is a read-modify-write under optimistic concurrency. Two admins
// editing at once means the second save is REFUSED, not silently merged, which
// is the same contract as the stage mapping next to it.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable, type HttpsCallableResult } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { TAXONOMY_CALLABLE, GET_ROSTER_ACTION, SAVE_ROSTER_ACTION } from './reportContract';
import { invalidateTrackerReports } from './reportCache';

export type RosterDisposition = 'broker' | 'support' | 'group';

/** Mirrors RosterGroupSchema. */
export interface RosterGroup {
  key: string;
  label: string;
  note?: string;
}

/** Mirrors RosterEntrySchema. */
export interface RosterEntry {
  adviserKey: string;
  adviserName?: string;
  disposition: RosterDisposition;
  groupKey?: string;
  note?: string;
}

/**
 * Mirrors RosterAnalystSchema: a credit analyst is the Salestrekker deal LABEL the practice puts on
 * a file the analyst is working, never a deal owner, so an analyst never appears as a broker.
 */
export interface RosterAnalyst {
  label: string;
  name?: string;
}

/** Matching form of an analyst label, mirroring normaliseAnalystLabel on the server. */
export const matchAnalystLabel = (label: string): string => label.trim().toLowerCase();

const NO_ANALYSTS: RosterAnalyst[] = [];

interface RosterDoc {
  defaultDisposition: 'broker' | 'support';
  groups: RosterGroup[];
  entries: RosterEntry[];
  /** Absent on a document saved before the analyst view existed. */
  analysts?: RosterAnalyst[];
  version: number;
  updatedAt: string;
  updatedBy: string;
}

export interface SaveOutcome {
  ok: boolean;
  /** Set when another editor saved first; the caller should reload, not retry. */
  conflict?: boolean;
  message?: string;
}

/**
 * Matching form of an adviser key, mirroring normaliseAdviserKey on the server.
 *
 * Must not diverge, or the client would believe it is replacing an entry while
 * the server refuses the save as a duplicate for the same person.
 */
export const matchAdviserKey = (key: string): string =>
  key.replace(/\s+/g, ' ').trim().toLowerCase();

/** A group key the server will accept: lower snake_case, derived from the label. */
export const groupKeyFromLabel = (label: string): string => {
  const slug = label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([^a-z])/, 'g$1');
  return slug === '' ? `group_${Date.now()}` : slug.slice(0, 64);
};

/**
 * @param companyId tenant whose roster is being read.
 * @param enabled   false for a viewer who may not edit, so their page makes no
 *                  call at all rather than one that will be denied.
 */
export function useBrokerRoster(companyId: string | undefined, enabled: boolean) {
  const [doc, setDoc] = useState<RosterDoc | null>(null);
  const [isSeed, setIsSeed] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!companyId || !enabled) return;
    setLoading(true);
    setError(null);
    try {
      const call = httpsCallable(functions, TAXONOMY_CALLABLE);
      const res = (await call({
        action: GET_ROSTER_ACTION,
        companyId,
      })) as HttpsCallableResult<{ roster: RosterDoc; isSeed: boolean }>;
      setDoc(res.data.roster);
      setIsSeed(res.data.isSeed);
    } catch (err) {
      console.error('[PipelineTracker] getRoster failed', err);
      setError('The broker list could not be loaded, so it cannot be edited right now.');
    } finally {
      setLoading(false);
    }
  }, [companyId, enabled]);

  useEffect(() => {
    void load();
  }, [load]);

  /** What each person is currently classified as, keyed by matching form. */
  const byKey = useMemo(() => {
    const out = new Map<string, RosterEntry>();
    for (const entry of doc?.entries ?? []) out.set(matchAdviserKey(entry.adviserKey), entry);
    return out;
  }, [doc]);

  /**
   * Write the whole document.
   *
   * Every mutation below funnels through here rather than each building its own
   * payload, so the optimistic-concurrency handling and the conflict message
   * exist once. A partial save is not offered because the server replaces the
   * document: removing the last member of a group has to be distinguishable
   * from not having touched it.
   */
  const commit = useCallback(
    async (
      next: Pick<RosterDoc, 'defaultDisposition' | 'groups' | 'entries' | 'analysts'>,
      busyKey: string
    ): Promise<SaveOutcome> => {
      if (!companyId || !doc) return { ok: false, message: 'Not ready.' };
      setSaving(busyKey);
      try {
        const call = httpsCallable(functions, TAXONOMY_CALLABLE);
        const res = (await call({
          action: SAVE_ROSTER_ACTION,
          companyId,
          defaultDisposition: next.defaultDisposition,
          groups: next.groups,
          entries: next.entries,
          // Sent whole, like the rest. Omitted only when this document has never carried any, in
          // which case the server keeps whatever is stored rather than clearing it.
          ...(next.analysts !== undefined ? { analysts: next.analysts } : {}),
          expectedVersion: doc.version,
        })) as HttpsCallableResult<{ version: number; emptyGroups: string[] }>;

        setDoc({ ...doc, ...next, version: res.data.version });
        setIsSeed(false);
        invalidateTrackerReports();
        return { ok: true };
      } catch (err) {
        const code = (err as { code?: string }).code ?? '';
        if (code.endsWith('aborted')) {
          // Someone else saved between our load and our write. Retrying would
          // overwrite their edit, so the only correct move is to reload and let
          // this admin see what changed before deciding again.
          console.error('[PipelineTracker] saveRoster version conflict', err);
          void load();
          return {
            ok: false,
            conflict: true,
            message:
              'Someone else changed the broker list while this page was open. Reloaded; check it and try again.',
          };
        }
        console.error('[PipelineTracker] saveRoster failed', err);
        return { ok: false, message: 'That change could not be saved. Nothing was changed.' };
      } finally {
        setSaving(null);
      }
    },
    [companyId, doc, load]
  );

  /**
   * Classify one person.
   *
   * `adviserName` is stored for display in this screen only. The report always
   * prefers the name in the current snapshot, so a person who is renamed in the
   * CRM does not appear twice, once as they are and once as we remember them.
   */
  const setDisposition = useCallback(
    async (
      adviserKey: string,
      adviserName: string,
      disposition: RosterDisposition,
      groupKey?: string
    ): Promise<SaveOutcome> => {
      if (!doc) return { ok: false, message: 'Not ready.' };
      const key = matchAdviserKey(adviserKey);
      const others = doc.entries.filter((entry) => matchAdviserKey(entry.adviserKey) !== key);
      const entry: RosterEntry = {
        adviserKey,
        adviserName,
        disposition,
        ...(disposition === 'group' && groupKey !== undefined ? { groupKey } : {}),
      };
      return commit({ ...doc, entries: [...others, entry] }, adviserKey);
    },
    [doc, commit]
  );

  /** Return a person to the unclassified state, so the default decides again. */
  const clearDisposition = useCallback(
    async (adviserKey: string): Promise<SaveOutcome> => {
      if (!doc) return { ok: false, message: 'Not ready.' };
      const key = matchAdviserKey(adviserKey);
      return commit(
        { ...doc, entries: doc.entries.filter((e) => matchAdviserKey(e.adviserKey) !== key) },
        adviserKey
      );
    },
    [doc, commit]
  );

  const addGroup = useCallback(
    async (label: string): Promise<SaveOutcome> => {
      if (!doc) return { ok: false, message: 'Not ready.' };
      const trimmed = label.trim();
      if (trimmed === '') return { ok: false, message: 'Give the book a name first.' };
      const key = groupKeyFromLabel(trimmed);
      if (doc.groups.some((group) => group.key === key)) {
        return { ok: false, message: 'There is already a book with that name.' };
      }
      return commit({ ...doc, groups: [...doc.groups, { key, label: trimmed }] }, key);
    },
    [doc, commit]
  );

  /**
   * Remove a group, returning its members to unclassified.
   *
   * Both halves in ONE write. Deleting the group first would leave entries
   * pointing at a group that no longer exists, which the server refuses, and an
   * operator would be stuck with a roster they could not save at all.
   */
  const removeGroup = useCallback(
    async (groupKey: string): Promise<SaveOutcome> => {
      if (!doc) return { ok: false, message: 'Not ready.' };
      return commit(
        {
          ...doc,
          groups: doc.groups.filter((group) => group.key !== groupKey),
          entries: doc.entries.filter((entry) => entry.groupKey !== groupKey),
        },
        groupKey
      );
    },
    [doc, commit]
  );

  /** What an unclassified person is treated as. Broker by default. */
  const setDefaultDisposition = useCallback(
    async (next: 'broker' | 'support'): Promise<SaveOutcome> => {
      if (!doc) return { ok: false, message: 'Not ready.' };
      return commit({ ...doc, defaultDisposition: next }, '__default__');
    },
    [doc, commit]
  );

  /**
   * Replace the analyst list.
   *
   * The whole list in one write, so adding Sam and renaming Beth in one sitting cannot leave the
   * roster half-saved. Blank labels are dropped and names trimmed here, so the server never has to
   * refuse an empty row the admin simply did not fill in.
   */
  const setAnalysts = useCallback(
    async (next: RosterAnalyst[]): Promise<SaveOutcome> => {
      if (!doc) return { ok: false, message: 'Not ready.' };
      const seen = new Set<string>();
      const analysts: RosterAnalyst[] = [];
      for (const analyst of next) {
        const label = analyst.label.trim();
        if (label === '') continue;
        const key = matchAnalystLabel(label);
        if (seen.has(key)) {
          return { ok: false, message: `"${label}" is listed twice. Each label can mark one analyst.` };
        }
        seen.add(key);
        const name = analyst.name?.trim() ?? '';
        analysts.push(name !== '' && name !== label ? { label, name } : { label });
      }
      return commit({ ...doc, analysts }, '__analysts__');
    },
    [doc, commit]
  );

  // Stable between renders: the analyst editor resets its draft when this changes, and a fresh
  // empty array on every render would reset it on every keystroke.
  const analysts = useMemo(() => doc?.analysts ?? NO_ANALYSTS, [doc]);

  return {
    analysts,
    setAnalysts,
    groups: doc?.groups ?? [],
    entries: doc?.entries ?? [],
    byKey,
    defaultDisposition: doc?.defaultDisposition ?? 'broker',
    version: doc?.version ?? null,
    ready: doc !== null,
    /** True when nothing has been saved, so the screen can say so plainly. */
    isSeed,
    loading,
    error,
    /** The key currently being written, so one row can show a spinner. */
    saving,
    setDisposition,
    clearDisposition,
    addGroup,
    removeGroup,
    setDefaultDisposition,
    reload: load,
  };
}
